// @flow

import React from 'react';
import styled from 'styled-components';
import Observer from 'react-intersection-observer';
import { calculateSizes } from './helpers';
import DimensionContext from '../../context/dimensionContext';
import type { Theme } from '../../types';

const StyledImage = styled<any, Theme>('img')`
  max-width: 100%;
  align-self: center;
`;

/*
Min-height to fix Observer bug.
I think no height means not in view so img so inView is false?
*/
const StyledRef = styled<any, Theme>('div')`
  min-height: 1px;
`;

type Props = {|
  alt?: string,
  className?: string,
  originalImgWidth?: ?number,
  src: string,
  srcSet: Array<{|
    width: number,
    url: string,
  |}>,
|};

/**
 * OK. A lot of questions about the responsive image arise. So here is a small explanation.
 * In responsive images, there are two important attributes that are added to HTML's native
 * <img /> component:
 *
 * SrcSet
 * Defines which resources you have available. Per resource, you can indicate the *intrinsic*
 * width of the resource. This is your way to tell the browser "the image with URL x is 200px width"
 *
 * Sizes
 * Defines how large your image is going to be on the page. This attributes allows media queries and
 * px / em / rem / vw units.
 *
 * With the combination of both attributes, your browser can determine what the best resource is to
 * load. It determines the image width in your page based on the media queries in sizes and matches
 * one of the resources given in the srcSet that matches that width the best.
 *
 * You might wonder why you have to define the sizes attribute. Your browser knows how big
 * the image is, right? Well, not exactly. Your browser would like to download images ASAP. That
 * means it is not going to wait until all CSS and DOM have been parsed before it starts loading
 * the image. That's why you need to give some pointers to the browser how big the image is going
 * to be.
 *
 * Resources:
 * https://developer.mozilla.org/en-US/docs/Web/HTML/Element/img
 * https://cloudfour.com/thinks/responsive-images-101-definitions/
 */
const ResponsiveImage = ({
  alt,
  className,
  originalImgWidth,
  src,
  srcSet,
}: Props) => {
  const maxImgWidth = originalImgWidth || [...srcSet].sort((a, b) => {
    const aWidth = a.width;
    const bWidth = b.width;

    if (bWidth > aWidth) {
      return 1;
    }

    if (bWidth < aWidth) {
      return -1;
    }

    return 0;
  })[0].width;

  return (
    <Observer rootMargin="0px 0px 150px 0px" triggerOnce>
      {({ inView, ref }) => (
        <StyledRef ref={ref}>
          {inView && (
            <DimensionContext.Consumer>
              {dimensions => (
                <StyledImage
                  alt={alt}
                  className={className}
                  sizes={calculateSizes(dimensions, maxImgWidth)}
                  src={src}
                  srcSet={srcSet.map(({ width, url }) => `${url} ${width}w`).join(',')}
                  width={originalImgWidth}
                />
              )}
            </DimensionContext.Consumer>
          )}
        </StyledRef>
      )}
    </Observer>
  );
};

ResponsiveImage.defaultProps = {
  alt: '',
  className: '',
  originalImgWidth: null,
};

export default ResponsiveImage;
